import { GWCA } from '~/lib/ca-constants';
import { GWUtils } from '~/utils/ca-utils';
import { GWCustomerAccountsTab } from './ca-tab';

customElements.define(
  'gw-customer-accounts-dashboard',
  class GWCustomerAccountsDashboard extends GWCustomerAccountsTab {
    constructor() {
      super();
    }

    connectedCallback() {
      super.connectedCallback();

      this._initDashboard();
    }

    /**
     * Initialize the dashboard by rendering various sections.
     */
    async _initDashboard() {
      if (GWUtils.hasCustomer) {
        this._renderRecentlyViewedCollections();
      }

      this.loadSavedCartItems();
      this.loadFavoriteItems();
      this.loadRecentlyViewedItems();
    }

    async loadRecentlyViewedItems(props = {}) {
      const { preventDrawerRender = false, preventCarouselRender = false, page = 1, limit = 8 } = props;

      try {
        const recentlyViewedItems = await this._getRecentlyViewedItems(page, limit);

        this._renderRecentlyViewedItems(recentlyViewedItems, {
          preventDrawerRender,
          preventCarouselRender,
          page,
          limit,
        });
      } catch (error) {
        console.error('Error rendering recently viewed items:', error);
      }
    }

    /**
     * Gets recently viewed items, either from API (if user is logged in) or from local storage.
     * @param {number} page - Current page for pagination.
     * @param {number} limit - Number of items per page.
     * @returns {Promise<Array>} - Array of recently viewed items.
     */
    async _getRecentlyViewedItems(page, limit) {
      if (!GWUtils.hasCustomer) {
        // Not logged in — get from local storage.
        return JSON.parse(localStorage.getItem(GWCA.STORAGE_KEYS.TEMP_RECENTLY_VIEWED_ITEMS)) || [];
      }

      // Logged in — fetch from API.
      const response = await GWUtils.request(
        GWCA.ENDPOINTS.VIEWED_PRODUCTS.GET,
        {
          customerID: GWUtils.customerId,
          limit,
          page,
        },
        'GET'
      );

      if (response.error) {
        throw new Error(response.error);
      }

      return response.products || [];
    }

    /**
     * Renders the given recently viewed items into the carousel/drawer.
     * @param {Array} items - The recently viewed items to render.
     * @param {Object} props - Additional rendering props.
     * @param {boolean} props.preventDrawerRender - Toggle drawer rendering.
     * @param {boolean} props.preventCarouselRender - Toggle carousel rendering.
     * @param {number} props.page - Current page.
     * @param {number} props.limit - Page limit.
     */
    _renderRecentlyViewedItems(items, props) {
      const recentlyViewedContainerSelector = '[data-recently-viewed-container]';
      const recentlyViewedDrawerContainerSelector = '[data-recently-viewed-drawer-container]';

      const recentlyViewedContainer = GWUtils.appShadow.querySelector(recentlyViewedContainerSelector);
      if (!recentlyViewedContainer) return;

      if (!items.length) {
        this._renderEmptyState(recentlyViewedContainerSelector, recentlyViewedDrawerContainerSelector);
        return;
      }

      // Render to carousel
      if (!props.preventCarouselRender) {
        this._renderProductsToCarousel(items, recentlyViewedContainerSelector);
      }

      // Render to drawer
      if (!props.preventDrawerRender) {
        this._renderProductsToDrawer({
          products: items,
          containerSelector: recentlyViewedDrawerContainerSelector,
          productTemplateId: GWCA.TEMPLATE_IDS.PRODUCT_CARD,
          currentPage: props.page,
          nextPage: null,
        });
      }
    }

    /**
     * Gets saved cart items, either from API (if user is logged in) or from local storage.
     * @param {number} page - Current page for pagination.
     * @param {number} limit - Number of items per page.
     * @returns {Promise<Array>} - Array of saved cart items.
     */
    async _getSavedCartItems(page, limit) {
      if (!GWUtils.customerId) {
        // Not logged in — get from local storage.
        return JSON.parse(localStorage.getItem(GWCA.STORAGE_KEYS.TEMP_CART_PRODUCTS)) || [];
      }

      // Logged in — fetch from API.
      const response = await GWUtils.request(
        GWCA.ENDPOINTS.CART.GET,
        { customerEmail: GWUtils.customerEmail, limit, page },
        'GET'
      );

      if (response.error) {
        throw new Error(response.error);
      }

      return response.products || [];
    }

    /**
     * Renders the given cart items into the carousel/drawer.
     * @param {Array} items - The cart items to render.
     * @param {Object} props - Additional rendering props.
     * @param {boolean} props.preventDrawerRender - Toggle drawer rendering.
     * @param {boolean} props.preventCarouselRender - Toggle carousel rendering.
     * @param {number} props.page - Current page.
     * @param {number} props.limit - Page limit.
     */
    _renderSavedCartItems(items, props) {
      const savedItemsContainerSelector = '[data-saved-from-cart-items-container]';
      const savedItemsDrawerContainerSelector = '[data-saved-from-cart-drawer-container]';

      const savedItemsContainer = this.querySelector(savedItemsContainerSelector);
      if (!savedItemsContainer) return;

      if (!items.length) {
        this._renderEmptyState(savedItemsContainerSelector, savedItemsDrawerContainerSelector);
        return;
      }

      // Calculate next page for pagination
      const nextPage = this._calculateNextPage({
        totalItems: items.length,
        currentPage: props.page,
        limit: props.limit,
      });

      // Render to carousel
      if (!props.preventCarouselRender) {
        this._renderProductsToCarousel(items, savedItemsContainerSelector);
      }

      // Render to drawer
      if (!props.preventDrawerRender) {
        this._renderProductsToDrawer({
          products: items,
          containerSelector: savedItemsDrawerContainerSelector,
          removeButtonAction: 'data-saved-from-cart-remove',
          productTemplateId: GWCA.TEMPLATE_IDS.FORM_PRODUCT_CARD,
          currentPage: props.page,
          nextPage: nextPage,
        });
      }
    }

    /**
     * Main method to fetch & render cart items from API or local storage.
     * @param {Object} [props={}] - Additional rendering props.
     */
    async loadSavedCartItems(props = {}) {
      const { preventDrawerRender = false, preventCarouselRender = false, page = 1, limit = 10 } = props;

      try {
        const savedItems = await this._getSavedCartItems(page, limit);
        this._renderSavedCartItems(savedItems, {
          preventDrawerRender,
          preventCarouselRender,
          page,
          limit,
        });
      } catch (error) {
        console.error('Error rendering saved items from cart:', error);
      }
    }

    /**
     * Retrieves favorite items from either the API (if logged in)
     * or local storage (if not).
     * @param {number} page
     * @param {number} limit
     * @returns {Promise<Array>} favorite items
     */
    async _getFavoriteItems(page, limit) {
      if (!GWUtils.hasCustomer) {
        // Not logged in: local storage
        return JSON.parse(localStorage.getItem(GWCA.STORAGE_KEYS.TEMP_WISHLIST_PRODUCTS)) || [];
      }

      // Logged in: API request
      const response = await GWUtils.request(
        GWCA.ENDPOINTS.FAVORITES.GET,
        { limit, page, customerID: GWUtils.customerId },
        'GET'
      );

      if (response.error) {
        throw new Error(response.error);
      }

      return response.products || [];
    }

    /**
     * Renders the given items into carousel and drawer
     * @param {Array} favoriteItems
     * @param {Object} props
     */
    _renderFavoriteItems(favoriteItems, props) {
      const favoritesContainerSelector = '[data-favorites-container]';
      const favoritesDrawerContainerSelector = '[data-favorites-drawer-container]';

      const favoritesContainer = GWUtils.appShadow.querySelector(favoritesContainerSelector);
      if (!favoritesContainer) return;

      if (!favoriteItems.length) {
        this._renderEmptyState(
          favoritesContainerSelector,
          favoritesDrawerContainerSelector,
          'You have no favorite items.'
        );
        return;
      }

      const { preventDrawerRender = false, preventCarouselRender = false, page = 1, limit = 10 } = props;

      // Carousel
      if (!preventCarouselRender) {
        // Reverse if needed
        this._renderProductsToCarousel(favoriteItems, favoritesContainerSelector);
      }

      // Calculate next page
      const nextPage = this._calculateNextPage({
        totalItems: favoriteItems.length,
        currentPage: page,
        limit: limit,
      });

      // Drawer
      if (!preventDrawerRender) {
        this._renderProductsToDrawer({
          products: favoriteItems,
          containerSelector: favoritesDrawerContainerSelector,
          removeButtonAction: 'data-wishlist-remove',
          productTemplateId: GWCA.TEMPLATE_IDS.FORM_PRODUCT_CARD,
          currentPage: page,
          nextPage: nextPage,
        });
      }
    }

    /**
     * Renders a single favorite item to the carousel and drawer.
     * The diffrence between this and _renderFavoriteItems is that this
     * method only renders a single item and prepends it to the list without
     * re-fetching and re-rendering the entire list.
     * @param {Object} product - The product to render.
     * @returns {void}
     */
    _renderSingleFavoriteItem(product) {
      const favoritesContainerSelector = '[data-favorites-container]';
      const favoritesDrawerContainerSelector = '[data-favorites-drawer-container]';

      // Create a carousel element and prepend it to the carousel container
      const favoritesContainer = GWUtils.appShadow.querySelector(favoritesContainerSelector);

      const productElementTemplate = document.getElementById(GWCA.TEMPLATE_IDS.PRODUCT_CARD);
      if (!productElementTemplate) {
        console.warn('Product card template not found.');
        return;
      }
      const productElement = productElementTemplate.content.cloneNode(true);
      const carouselElement = this._populateCarouselItem(productElement, product);

      const favoritesCarousel = favoritesContainer.querySelector('sl-carousel');
      if (favoritesCarousel) {
        favoritesCarousel.prepend(carouselElement);
      } else {
        const carousel = document.createElement('sl-carousel');
        carousel.setAttribute('navigation', '');
        carousel.setAttribute('slides-per-page', '3');
        carousel.setAttribute('slides-per-move', '3');
        carousel.setAttribute('style', '--scroll-hint: 5%; --aspect-ratio: none');

        carousel.appendChild(carouselElement);
        favoritesContainer.innerHTML = '';
        favoritesContainer.appendChild(carousel);
      }

      // Create a drawer element and prepend it to the drawer container
      const drawerContainer = GWUtils.appShadow.querySelector(favoritesDrawerContainerSelector);
      if (drawerContainer) {
        const drawerElementTemplate = document.getElementById(GWCA.TEMPLATE_IDS.FORM_PRODUCT_CARD);
        if (!drawerElementTemplate) {
          console.warn('Drawer product card template not found.');
          return;
        }

        const drawerElement = this._createProductElement(product, drawerElementTemplate, 'data-wishlist-remove');

        this._ensureDrawerNotEmpty(favoritesContainer);

        drawerContainer.prepend(drawerElement);
      }
    }

    /**
     * Removes a favorite item from the carousel and drawer.
     * @param {number} productId - The ID of the product to remove.
     * @returns {void}
     */
    _removeFavoriteItem(productId) {
      const favoritesContainerSelector = '[data-favorites-container]';
      const favoritesDrawerContainerSelector = '[data-favorites-drawer-container]';

      const favoritesContainer = GWUtils.appShadow.querySelector(favoritesContainerSelector);
      const favoritesDrawerContainer = GWUtils.appShadow.querySelector(favoritesDrawerContainerSelector);

      if (favoritesContainer) {
        const carouselItem = favoritesContainer.querySelector(`[data-product-id="${productId}"]`);
        if (carouselItem) carouselItem.remove();

        // If carousel is empty, render empty state
        if (!favoritesContainer.querySelector('sl-carousel-item')) {
          this._renderEmptyState(
            favoritesContainerSelector,
            favoritesDrawerContainerSelector,
            'You have no favorite items.'
          );
        }
      }

      if (favoritesDrawerContainer) {
        const drawerItem = favoritesDrawerContainer.querySelector(`[data-product-id="${productId}"]`);
        if (drawerItem) drawerItem.remove();
      }
    }

    /**
     * Main orchestrator to fetch and render favorite items.
     * @param {Object} [props={}]
     */
    async loadFavoriteItems(props = {}) {
      const { page = 1, limit = 10 } = props;

      try {
        const favoriteItems = await this._getFavoriteItems(page, limit);
        this._renderFavoriteItems(favoriteItems, props);
      } catch (error) {
        console.error('Error rendering favorite items:', error);
      }
    }

    /**
     * Renders recently viewed collections as a carousel.
     */
    _renderRecentlyViewedCollections() {
      const storageKey = GWCA.STORAGE_KEYS.RECENTLY_VIEWED_COLLECTION;
      const collectionsData = JSON.parse(localStorage.getItem(storageKey)) || [];

      const container = this.querySelector('[data-recently-viewed-collections-container]');
      if (!container) return;

      container.innerHTML = '';

      if (!collectionsData.length) {
        return;
      }

      const carousel = document.createElement('sl-carousel');
      carousel.setAttribute('navigation', '');
      carousel.setAttribute('style', '--scroll-hint: 5%; --aspect-ratio: none');

      collectionsData.forEach((collection) => {
        const item = document.createElement('sl-carousel-item');
        item.innerHTML = `
          <a href="${collection.url}" class="gw-button-text gw-unstyled-link gw-recently-viewed-collection">
            ${collection.title}
          </a>
        `;
        carousel.appendChild(item);
      });

      container.appendChild(carousel);
    }

    /**
     * Renders a list of products into a carousel in the specified container.
     * @param {Array} products - The product list to render.
     * @param {string} containerSelector - The CSS selector for the carousel container.
     */
    _renderProductsToCarousel(products, containerSelector) {
      const container = GWUtils.appShadow.querySelector(containerSelector);
      if (!container) return;

      container.innerHTML = '';

      this._ensureDrawerNotEmpty(container);

      const carousel = document.createElement('sl-carousel');
      carousel.setAttribute('navigation', '');
      carousel.setAttribute('slides-per-page', '3');
      carousel.setAttribute('slides-per-move', '3');
      carousel.setAttribute('style', '--scroll-hint: 5%; --aspect-ratio: none');

      if (products.length > 3) {
        carousel.setAttribute('data-has-next', '');
      }

      carousel.addEventListener('sl-slide-change', (event) => {
        event.detail.index > 0
          ? carousel.setAttribute('data-has-previous', '')
          : carousel.removeAttribute('data-has-previous');

        event.detail.index + 3 < products.length
          ? carousel.setAttribute('data-has-next', '')
          : carousel.removeAttribute('data-has-next');
      });

      const template = document.getElementById(GWCA.TEMPLATE_IDS.PRODUCT_CARD);
      if (!template) {
        console.warn('Carousel item template not found.');
        return;
      }

      products.forEach((product) => {
        if (this._shouldHideProduct(product)) return;

        const productElement = template.content.cloneNode(true);
        const carouselElement = this._populateCarouselItem(productElement, product);
        carousel.appendChild(carouselElement);
      });

      container.appendChild(carousel);
    }

    /**
     * Renders products with a quick-add-to-cart form.
     * This method handles both variant and single-variant products.
     * @param {Object} props - Properties for rendering the product list.
     * @param {Array} props.products - The products to render.
     * @param {number} props.currentPage - The current page number.
     * @param {number} props.nextPage - The next page number.
     * @param {string} props.containerSelector - The container's CSS selector.
     * @param {string} props.removeButtonAction - The attribute action for remove buttons.
     * @param {string} props.productTemplateId - The ID of the product template.
     */
    _renderProductsToDrawer({
      products,
      currentPage,
      nextPage,
      containerSelector,
      removeButtonAction,
      productTemplateId,
    }) {
      const container = GWUtils.appShadow.querySelector(containerSelector);
      if (!container) return;

      // Clear container if rendering the first page
      if (currentPage === 1) {
        container.innerHTML = '';
      }

      // Remove load more button if it exists
      const loadMoreButton = container.querySelector('[data-load-more-button]');
      if (loadMoreButton) loadMoreButton.remove();

      const template = document.getElementById(productTemplateId || GWCA.TEMPLATE_IDS.PRODUCT_CARD);
      if (!template) {
        console.warn('Product card template not found.');
        return;
      }

      products.forEach((product) => {
        if (this._shouldHideProduct(product)) return;

        const productElement = this._createProductElement(product, template, removeButtonAction);
        container.appendChild(productElement);
      });

      if (nextPage) {
        this._renderLoadMoreButton(container, nextPage);
      }
    }

    /**
     * Renders an empty state message in a container.
     * If a drawer is associated with the container, marks it as empty.
     * @param {string} carouselItemsContainerSelctor - The CSS selector for the carousel container.
     * @param {string} drawerItemsContainerSelector - The CSS selector for the drawer container.
     * @param {string} drawerEmptyStateMessage - The message to display.
     */
    _renderEmptyState(carouselItemsContainerSelctor, drawerItemsContainerSelector, drawerEmptyStateMessage) {
      const carouselContainer = GWUtils.appShadow.querySelector(carouselItemsContainerSelctor);
      if (!carouselContainer) return;

      const drawerSelector = carouselContainer.getAttribute('data-drawer-selector');
      const emptyStateMessage = carouselContainer.getAttribute('data-empty-state-message') || 'No items found.';

      if (drawerSelector) {
        const drawer = GWUtils.appShadow.querySelector(`#${drawerSelector}`);

        if (drawer) {
          drawer.setAttribute('empty', '');

          const drawerItemsContainer = drawer.querySelector(drawerItemsContainerSelector);
          if (drawerItemsContainer) {
            drawerItemsContainer.innerHTML = `
              <h2 class="gw-header drawer-empty-message">${drawerEmptyStateMessage}</h2>
            `;
          }

          if (drawer.hasAttribute('open')) {
            drawer.hide();
          }
        }
      }

      carouselContainer.innerHTML = `
        <div class="gw-empty-list">
          <div class="gw-empty-list-inner">
            <p>${emptyStateMessage}</p>
          </div>
        </div>
      `;
    }

    /**
     * Calculates the next page number based on the number of items and the current page.
     * @param {number} totalItems - The number of items in the list.
     * @param {number} currentPage - The current page number.
     * @param {number} limit - The number of items per page.
     * @returns {number} - The next page number.
     */
    _calculateNextPage({ totalItems, currentPage, limit }) {
      const totalPages = Math.ceil(totalItems / limit);
      return currentPage < totalPages ? currentPage + 1 : null;
    }

    /**
     * Renders a "Load More" button for the drawer.
     * @param {HTMLElement} container - The container element.
     * @param {number} nextPage - The next page number.
     * @returns {void}
     */
    _renderLoadMoreButton(container, nextPage) {
      const loadMoreButton = document.createElement('button');
      loadMoreButton.textContent = 'Load More';
      loadMoreButton.classList.add('gw-button', 'gw-button--stroke');
      loadMoreButton.setAttribute('data-load-more-button', '');

      const loaderHTML = `
        <span class="gw-loader-container" hidden data-loader>
          <span class="gw-loader"></span>
        </span>
      `;

      loadMoreButton.insertAdjacentHTML('beforeend', loaderHTML);

      loadMoreButton.addEventListener('click', () => {
        loadMoreButton.querySelector('[data-loader]').removeAttribute('hidden');

        this.loadFavoriteItems({
          preventCarouselRender: true,
          page: nextPage,
        });
      });

      container.appendChild(loadMoreButton);
    }

    /**
     * Determines if a product should be hidden based on its tags.
     * @param {Object} product - The product object.
     * @returns {boolean} True if product should be hidden, otherwise false.
     */
    _shouldHideProduct(product) {
      return product.tags?.some((tag) => GWCA.TAGS_TO_HIDE.includes(tag));
    }

    /**
     * Creates a product element from a template and sets up its form fields.
     * @param {Object} product - The product data.
     * @param {HTMLTemplateElement} template - The template for the product.
     * @param {string} removeButtonAction - The attribute for the remove button action.
     * @returns {DocumentFragment} - The populated product element.
     */
    _createProductElement(product, template, removeButtonAction) {
      const element = template.content.cloneNode(true);
      const productTitle = element.querySelector('[data-product-title]');
      const productLinks = element.querySelectorAll('[data-product-link]');
      const productImage = element.querySelector('[data-product-image]');
      const quickAddForm = element.querySelector('[data-quick-add-to-cart-form]');

      const priceEl = element.querySelector('[data-product-price]');
      priceEl.setAttribute('product-handle', product.handle);
      if (product.variant_id) priceEl.setAttribute('product-variant', product.variant_id);

      element.querySelector('.gw-product-card').setAttribute('data-product-id', GWUtils.parseVariantId(product.id));

      if (productLinks.length) {
        productLinks.forEach((productLink) => {
          productLink.href = `/products/${product.handle}`;
        });
      }

      if (productTitle) {
        productTitle.textContent = product.title;
      }

      if (productImage && product.featuredImage?.url) {
        productImage.src = product.featuredImage.url + '&width=300';

        if (product.featuredImage.altText) {
          productImage.alt = product.featuredImage.altText;
        }
      }

      if (quickAddForm) {
        // Handle variants
        this._populateVariantFields(product, quickAddForm);

        // Set product ID
        const productIdHiddenInput = document.createElement('input');
        productIdHiddenInput.type = 'hidden';
        productIdHiddenInput.name = 'productId';
        productIdHiddenInput.value = GWUtils.parseVariantId(product.id);
        quickAddForm.prepend(productIdHiddenInput);
      }

      // Set remove button action if applicable
      if (removeButtonAction) {
        const removeButton = element.querySelector('[data-remove-button]');
        if (removeButton) {
          removeButton.setAttribute(removeButtonAction, '');

          if (removeButtonAction === 'data-saved-from-cart-remove') {
            removeButton.setAttribute('aria-label', `Remove ${product.title} from saved items`);
          }

          if (removeButtonAction === 'data-wishlist-remove') {
            removeButton.setAttribute('aria-label', `Remove ${product.title} from favorites`);
          }
        }
      }

      // If there is a gw-wishlist-button element, populate it with the product ID
      const wishlistButton = element.querySelector('gw-wishlist-button');
      if (wishlistButton) {
        wishlistButton.setAttribute('product-id', GWUtils.parseVariantId(product.id));

        const scriptTag = document.createElement('script');
        scriptTag.type = 'application/json';
        scriptTag.setAttribute('data-product-data', '');
        scriptTag.textContent = JSON.stringify(product);

        wishlistButton.appendChild(scriptTag);
      }

      return element;
    }

    /**
     * Populates the variant fields for a product, creating a select if multiple variants exist.
     * @param {Object} product - The product object.
     * @param {HTMLFormElement} form - The form element where variants will be appended.
     */
    _populateVariantFields(product, form) {
      const variants = product.variants?.nodes || [];
      if (variants.length > 1) {
        const selectElement = document.createElement('select');
        selectElement.name = 'id';
        selectElement.classList.add('gw-product-card__variant-selector');

        variants.forEach((variant) => {
          const option = document.createElement('option');
          const variantId = GWUtils.parseVariantId(variant.id);
          option.value = variantId;
          option.textContent = variant.title;
          if (variantId == (product.variant_id || product.variantId)) {
            option.selected = true;
          }
          selectElement.appendChild(option);
        });

        form.prepend(selectElement);
      } else if (variants.length === 1) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'id';
        input.value =
          product.variant_id || product.variantId
            ? product.variant_id || product.variantId
            : GWUtils.parseVariantId(variants[0].id);
        form.prepend(input);
      }
    }

    /**
     * Updates the price display elements for a product.
     * @param {Object} product - The product object.
     * @param {HTMLElement} compareAtPrice - The compare at price element.
     * @param {HTMLElement} price - The price element.
     */
    _updatePriceDisplay(product, $compareAtPrice, $price) {
      let currentVariant = product.variants?.nodes[0];
      const productVariantId = product.variant_id || product.variantId;
      if (productVariantId) {
        const selectedVariant = product.variants?.nodes.find((variant) => {
          return GWUtils.parseVariantId(variant.id) == productVariantId;
        });

        if (selectedVariant) {
          currentVariant = selectedVariant;
        }
      }

      const compareAtPrice = currentVariant?.compareAtPrice || currentVariant?.compare_at_price;
      const price = currentVariant?.price;

      if ($compareAtPrice && compareAtPrice) {
        if (typeof compareAtPrice === 'number') {
          $compareAtPrice.textContent = GWUtils.formatMoney(compareAtPrice);
        } else {
          $compareAtPrice.textContent = compareAtPrice.includes('$') ? compareAtPrice : `$${compareAtPrice}`;
        }
      }

      if (price && $price) {
        if (typeof price === 'number') {
          $price.textContent = GWUtils.formatMoney(price);
        } else {
          const formattedPrice = '$' + price;
          $price.textContent = price.includes('$') ? price : formattedPrice;
        }
      }

      if (compareAtPrice && price) {
        $compareAtPrice.removeAttribute('hidden');
        $price.classList.add('gw-product-card__price--sale');
      }
    }

    /**
     * Ensures that the corresponding drawer for a container is not marked as empty.
     * @param {HTMLElement} container - The container element.
     */
    _ensureDrawerNotEmpty(container) {
      const drawerSelector = container.getAttribute('data-drawer-selector');
      if (drawerSelector) {
        const drawer = GWUtils.appShadow.querySelector(`#${drawerSelector}`);
        if (drawer) drawer.removeAttribute('empty');

        const drawerEmptyMessage = drawer.querySelector('.drawer-empty-message');
        if (drawerEmptyMessage) drawerEmptyMessage.remove();
      }
    }

    /**
     * Populates a carousel item with product information.
     * @param {DocumentFragment} productElement - The product element fragment.
     * @param {Object} product - The product object.
     * @returns {HTMLElement} - The populated carousel item.
     */
    _populateCarouselItem(productElement, product) {
      const populatedProductElement = this._populateProductCard(productElement, product);

      const carouselItem = document.createElement('sl-carousel-item');
      carouselItem.setAttribute('data-product-id', GWUtils.parseVariantId(product.id));
      carouselItem.appendChild(populatedProductElement);

      return carouselItem;
    }

    /**
     * Populates a product card element with product information.
     * @param {DocumentFragment} element - The product card element fragment.
     * @param {Object} product - The product object.
     */
    _populateProductCard(element, product) {
      const productTitle = element.querySelector('[data-product-title]');
      const productLink = element.querySelector('[data-product-link]');
      const productImage = element.querySelector('[data-product-image]');
      const priceEl = element.querySelector('[data-product-price]');

      if (productTitle) productTitle.textContent = product.title;
      if (productLink) productLink.href = `/products/${product.handle}`;
      if (productImage && product.featuredImage?.url) {
        productImage.src = product.featuredImage.url + '&width=300';

        if (product.featuredImage.altText) {
          productImage.alt = product.featuredImage.altText;
        }
      }

      priceEl.setAttribute('product-handle', product.handle);
      if (product.variant_id) priceEl.setAttribute('product-variant', product.variant_id);

      // If there is a gw-wishlist-button element, populate it with the product ID
      const wishlistButton = element.querySelector('gw-wishlist-button');
      if (wishlistButton) {
        wishlistButton.setAttribute('product-id', GWUtils.parseVariantId(product.id));

        const scriptTag = document.createElement('script');
        scriptTag.type = 'application/json';
        scriptTag.setAttribute('data-product-data', '');
        scriptTag.textContent = JSON.stringify(product);

        wishlistButton.appendChild(scriptTag);
      }

      return element;
    }
  }
);
